import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import store, { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import {
  assignExistingWorkerToCompanies,
  fetchAssignedCompanies,
  getRevokeBadgeInfo,
  setTemp,
} from 'modules/state-mgmt/onboarding';
import useLoadAllData from 'hooks/useLoadAllData';
import { prettifyErrorResponse } from 'utils';
import {
  classifyResume,
  getResumeAction,
  isArchivedProject,
  isResumePending,
  resolveResumeRoute,
  shouldResume,
  ResumeAction,
  IResumeEmployer,
} from 'utils/resumeProjectOnboarding';

/** What the post-`loadAllData` half of the CH-4652 resume needs to finish up. */
interface IResumeContext {
  workerId: string;
  projectId: string;
  mergedEmployers: IResumeEmployer[];
  wasAlreadyOnProject: boolean;
  hasNewEmployers: boolean;
  fallbackPath: string;
}

interface IUseResumeProjectOnboarding {
  /**
   * Called once the resume has settled, on every path including the failures. Callers use it to
   * drop their own button spinner — this hook owns `isResuming` but knows nothing about theirs.
   */
  onSettled?: () => void;
}

/**
 * CH-4652 — attach a just-authenticated existing worker to the project and employers that were
 * chosen before the duplicate-worker check sent them to sign in, then drop them on the right
 * onboarding step instead of an empty My Projects.
 *
 * Shared by BOTH sign-in paths, which is the point of it being a hook rather than living in
 * `useLogin`: the password path (`Login/hooks.ts`) and the OTP path (`VerifyOtp/hooks.ts`). A
 * worker who cannot remember their password and resets it mid-flow must end up in the same place
 * as one who typed it.
 *
 * Call `resumeProjectOnboarding(fallbackPath)` after the worker is loaded into the onboarding
 * slice. It returns true when it has taken responsibility for navigation, false when the caller
 * should carry on and navigate as it normally would.
 */
const useResumeProjectOnboarding = ({ onSettled }: IUseResumeProjectOnboarding = {}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isResuming, setIsResuming] = useState(false);
  const languageCode = useAppSelector(state => state.app?.languageCode);
  const [{ hasLoaded, isLoading: isLoadingData, errors: loadErrors }, loadAllData] = useLoadAllData();
  const resumeContextRef = useRef<IResumeContext | null>(null);

  /**
   * Everything below reads state with `store.getState()` rather than `useAppSelector`, and that is
   * deliberate. It all runs inside one async call stack started when the worker signs in —
   * authenticate → `fetchWorkerDetails` → the resume — with no render in between, so any selector
   * value captured at render is frozen from before the sign-in and `onboarding.id` would still
   * read as `null`. The two helpers below are the only writers of `onboarding.temp` here, split by
   * intent so each call site says plainly whether the marker survives.
   */

  /** Merge into `onboarding.temp`. Everything already there — including the marker — is kept. */
  const patchTemp = (overrides: Record<string, any>) => {
    dispatch(setTemp({ ...(store.getState().onboarding?.temp || {}), ...overrides }));
  };

  /**
   * Remove the one-shot CH-4652 marker, leaving the rest of `temp` alone. The route guard holds the
   * worker on `/login` while it is set, so every exit from the resume must reach this — including
   * the failure paths, or they would be stranded on the login screen.
   */
  const dropResumeMarker = () => {
    const { resumeProjectOnboarding: marker, ...temp } = store.getState().onboarding?.temp || {};
    dispatch(setTemp(temp));
  };

  /** Give up on the resume and land where an ordinary sign-in would have. */
  const abandonResume = (fallbackPath: string) => {
    resumeContextRef.current = null;
    dropResumeMarker();
    setIsResuming(false);
    onSettled?.();
    navigate(fallbackPath);
  };

  /**
   * Second half of the resume, once `loadAllData` has settled. It runs from an effect rather than
   * inline because `loadAllData`'s returned promise resolves as soon as its thunks are *dispatched*,
   * not when they settle — completion is only observable through `hasLoaded` / `errors`.
   */
  const finishResume = async (context: IResumeContext) => {
    try {
      // `fetchAssignedCompanies` is not registered in the loader slice, so `hasLoaded` never waited
      // for the copy `loadAllData` fired. Re-read it explicitly: its `fulfilled` *replaces* the
      // array while the assign only appended optimistically, so a freshly-assigned company can
      // otherwise disappear a beat after we navigate — and ConsentForm reads that very array.
      await dispatch(
        fetchAssignedCompanies({ workerId: context.workerId, projectId: context.projectId, languageCode }),
      ).unwrap();

      // `patchTemp`, not `dropResumeMarker`: the marker has to outlive this navigation. Clearing it
      // while still on `/login` opens a one-render window in which the guard's LOGIN branch sees a
      // logged-in worker with no resume pending and redirects to My Projects — that redirect is
      // applied *after* ours and silently replaces the wizard step we just picked. Observed on QA
      // 2026-09-19: the guard returned `/project-list` for `currentPath: '/login'` ~3ms after this.
      patchTemp({
        employers: context.mergedEmployers,
        addEmployerFlow: context.wasAlreadyOnProject,
        visitedWizardSteps: [],
      });

      const { onboarding: latestOnboarding, cache } = store.getState();
      navigate(resolveResumeRoute({ onboarding: latestOnboarding, cache, hasNewEmployers: context.hasNewEmployers }));

      // Off the sign-in screen now, so the guard's LOGIN branch no longer applies. The timeout lets
      // the router commit the new location first; this only touches the store, so it is safe to run
      // after the screen that started the resume has unmounted.
      setTimeout(dropResumeMarker, 0);
    } catch (error) {
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
      dropResumeMarker();
      navigate(context.fallbackPath);
    } finally {
      setIsResuming(false);
      onSettled?.();
    }
  };

  useEffect(() => {
    const context = resumeContextRef.current;
    if (!context || isLoadingData) return;
    // Nothing has been reported yet — `loadAllData` has not finished kicking off its thunks.
    if (!hasLoaded && !(loadErrors?.length > 0)) return;

    resumeContextRef.current = null;

    if (loadErrors?.length > 0) {
      enqueueSnackbar(loadErrors.map((error: any) => prettifyErrorResponse(error)).join(' '), { variant: 'error' });
      abandonResume(context.fallbackPath);
      return;
    }

    finishResume(context);
  }, [hasLoaded, isLoadingData, loadErrors]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Attach the employers the worker does not hold yet, then prove it actually happened.
   *
   * `assignExistingWorkerToCompanies` does not `.unwrap()` its inner thunk, so a failed assignment
   * still resolves as success — the very defect this ticket is about. Two other flows depend on
   * that behaviour, so rather than change the shared thunk, confirm the outcome the only way that
   * cannot lie: read the assignment back and check the companies we asked for are there. Throws if
   * any are still missing, which the caller turns into a toast.
   */
  const assignNewEmployers = async (workerId: string, projectId: string, newEmployers: IResumeEmployer[]) => {
    // Pass ONLY the genuinely-new employers, never the full selection. `buildAssignmentParams` does
    // dedupe, but it dedupes against `onboarding.assignedCompanies`; filtering up front means an
    // already-held company can never be re-POSTed even if that source ever under-reports.
    await dispatch(assignExistingWorkerToCompanies({ projectId, employers: newEmployers, languageCode })).unwrap();

    const confirmed = await dispatch(fetchAssignedCompanies({ workerId, projectId, languageCode })).unwrap();
    const stillMissing = newEmployers.filter(
      employer => !confirmed.some((company: any) => company.id === employer.value),
    );
    if (stillMissing.length > 0) {
      throw new Error(`Could not add ${stillMissing.map(e => e.label).join(', ')} to this project. Please try again.`);
    }
  };

  const resumeProjectOnboarding = async (fallbackPath: string): Promise<boolean> => {
    const state = store.getState();
    if (!shouldResume(state)) {
      // A marker can outlive its flow — the store is persisted, so an abandoned attempt survives
      // reloads. Drop it here rather than leaving it to hold the sign-in screen for this worker.
      if (isResumePending(state.onboarding, state.user)) dropResumeMarker();
      return false;
    }

    const workerId = state.onboarding.id;
    const projectId = state.onboarding.temp?.project?.value;

    setIsResuming(true);

    try {
      // Archived project — nothing to assign and nothing to onboard onto. Bail before any request.
      if (isArchivedProject(state.cache)) {
        abandonResume(fallbackPath);
        return true;
      }

      const { isRevoked = false } = await dispatch(
        getRevokeBadgeInfo({ workerId, projectId, languageCode: 'en' }),
      ).unwrap();
      if (isRevoked) {
        enqueueSnackbar(t('projectList.errors.registrationErrorBadgeRevoked'), { variant: 'error' });
        abandonResume(fallbackPath);
        return true;
      }

      // One project-scoped read answers everything the classification needs: which companies the
      // worker already holds here and, via each `workerProjectStatus`, whether the project sits in
      // their Current tab, their Pending tab, or both. It is also what
      // `assignExistingWorkerToCompanies` dedupes against and what ConsentForm reads, so it has to
      // be fetched regardless. See `classifyResume` for why this beats `lite/projects`.
      await dispatch(fetchAssignedCompanies({ workerId, projectId, languageCode })).unwrap();

      const { onboarding: loadedOnboarding, cache } = store.getState();
      const classification = classifyResume({ onboarding: loadedOnboarding, cache });
      const action = getResumeAction(classification);

      if (action === ResumeAction.SKIP) {
        abandonResume(fallbackPath);
        return true;
      }

      if (action === ResumeAction.ASSIGN) {
        await assignNewEmployers(workerId, projectId, classification.newEmployers);
      }

      resumeContextRef.current = {
        workerId,
        projectId,
        mergedEmployers: classification.mergedEmployers,
        wasAlreadyOnProject: classification.isOnProject,
        hasNewEmployers: classification.newEmployers.length > 0,
        fallbackPath,
      };

      // `employers` must be company ids: `useLoadAllData` uses the array directly as `companyIds`.
      // `workerId` must be explicit — it tests the parameter, not the resolved fallback.
      loadAllData({
        workerId,
        projectId,
        employers: classification.mergedEmployers.map(employer => employer.value),
      });

      return true;
    } catch (error) {
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
      abandonResume(fallbackPath);
      return true;
    }
  };

  return { isResuming, resumeProjectOnboarding };
};

export default useResumeProjectOnboarding;
